// recipeShares — data layer for user-to-user recipe sharing.
//
// Two paths, one table (recipe_shares, migration 0275):
//   • Link share   — createRecipeShare() with no recipient; returns a
//                    booda.app/r/<token> URL the sender copies. Anyone who
//                    opens it (signed in) can accept → their own copy.
//   • Direct share — createRecipeShare({ recipientId }) ALSO drops a
//                    notification (target_kind 'recipe_share') so the
//                    recipient gets a push and an accept/decline card.
//
// On accept the recipient gets an INDEPENDENT copy in their own
// user_recipes (the share carries a snapshot), so the sender editing or
// deleting the original never touches it.

import { supabase } from "./supabase";

// Universal-link base. Opening this routes through App.jsx's appUrlOpen
// handler (see the `/r/` branch) into the incoming-share accept screen.
// Requires the booda.app associated-domains / AASA setup to deep-link the
// native app; falls back to opening in the browser otherwise.
export const SHARE_LINK_BASE = "https://booda.app/r/";

export function buildShareUrl(token) {
  return `${SHARE_LINK_BASE}${encodeURIComponent(token)}`;
}

// Strip sender-private + transient fields so the recipient's copy is clean.
// Their pantry bindings, generation flags, and community attribution are
// meaningless (or wrong) on someone else's row. Keep the actual recipe
// content + the goal image.
function snapshotForShare(recipe) {
  if (!recipe || typeof recipe !== "object") return recipe;
  const {
    _gen, _genStartedAt, _serverPersisted, _buildIssues,
    _pairingActive, _isDreamMode, _priority, _initialServings,
    _communityRecipeId, communityRecipeId,
    shared, submittedForReview, reviewStatus,
    ...rest
  } = recipe;
  const ingredients = Array.isArray(rest.ingredients)
    ? rest.ingredients.map((ing) => {
        if (!ing || typeof ing !== "object") return ing;
        const { pantryItemId, ...keep } = ing;
        return keep;
      })
    : rest.ingredients;
  return { ...rest, ingredients };
}

/**
 * Create a share row. Returns { id, token, url }.
 * @param {object} args
 * @param {object} args.recipe          full recipe object to snapshot
 * @param {string} args.senderName      sender display name (denormalized)
 * @param {string} [args.recipientId]   direct-share target user id (omit for link)
 */
export async function createRecipeShare({ recipe, senderName, recipientId = null }) {
  const { data: sess } = await supabase.auth.getSession();
  const senderId = sess?.session?.user?.id;
  if (!senderId) throw new Error("Not signed in");
  if (!recipe?.title) throw new Error("Recipe has no title");

  const snapshot = snapshotForShare(recipe);
  const { data, error } = await supabase
    .from("recipe_shares")
    .insert({
      sender_id: senderId,
      sender_name: senderName || null,
      recipient_id: recipientId,
      recipe_snapshot: snapshot,
      recipe_title: recipe.title,
      recipe_emoji: recipe.emoji || "🍽️",
    })
    .select("id, token")
    .single();
  if (error) throw error;

  // Direct share → in-app notification + push. The notifications trigger
  // (migration 0053) fans this out to send-push automatically. target_kind
  // 'recipe_share' + the share id let the tap open the accept/decline card.
  if (recipientId) {
    await supabase.from("notifications").insert({
      user_id: recipientId,
      actor_id: senderId,
      msg: `shared "${recipe.title}" with you`,
      emoji: recipe.emoji || "🍽️",
      kind: "info",
      target_kind: "recipe_share",
      target_id: data.id,
    }).then(({ error: nErr }) => {
      if (nErr) console.warn("[recipeShares] notification insert failed:", nErr.message);
    });
  }

  return { id: data.id, token: data.token, url: buildShareUrl(data.token) };
}

/** Read a link share by token (SECURITY DEFINER RPC — works pre-claim). */
export async function getRecipeShareByToken(token) {
  if (!token) return null;
  const { data, error } = await supabase.rpc("get_recipe_share", { p_token: token });
  if (error) { console.warn("[recipeShares] get_recipe_share failed:", error.message); return null; }
  const row = Array.isArray(data) ? data[0] : data;
  return row || null;
}

/** Read a direct share by id (RLS: sender or recipient). */
export async function getRecipeShareById(id) {
  if (!id) return null;
  const { data, error } = await supabase
    .from("recipe_shares")
    .select("id, sender_id, sender_name, recipient_id, recipe_snapshot, recipe_title, recipe_emoji, status")
    .eq("id", id)
    .maybeSingle();
  if (error) { console.warn("[recipeShares] getById failed:", error.message); return null; }
  return data || null;
}

/**
 * Accept a share: persist the snapshot as the recipient's own recipe, then
 * mark the share accepted (binding the recipient on a link claim).
 * @param {object} share     a row from get/getById (needs id, token?, recipe_snapshot)
 * @param {Function} saveRecipe  useUserRecipes().saveRecipe — (recipe, source) => row
 * @returns {Promise<object>} the saved user_recipes row
 */
export async function acceptRecipeShare(share, saveRecipe) {
  if (!share?.recipe_snapshot) throw new Error("Share has no recipe");
  if (typeof saveRecipe !== "function") throw new Error("saveRecipe required");
  const saved = await saveRecipe(
    { ...share.recipe_snapshot, sharedFrom: share.sender_name || null },
    "ai",
  );
  const { error } = await supabase.rpc("respond_recipe_share", {
    p_share_id: share.id || null,
    p_token: share.token || null,
    p_status: "accepted",
    p_accepted_recipe_id: saved?.id || null,
  });
  if (error) console.warn("[recipeShares] respond(accept) failed:", error.message);
  return saved;
}

/** Decline a share (no copy made). */
export async function declineRecipeShare(share) {
  const { error } = await supabase.rpc("respond_recipe_share", {
    p_share_id: share?.id || null,
    p_token: share?.token || null,
    p_status: "declined",
    p_accepted_recipe_id: null,
  });
  if (error) console.warn("[recipeShares] respond(decline) failed:", error.message);
}
